import React, { useEffect, useMemo, useState, useRef } from 'react';
/**
 * TextHighlightRenderer — Select word/segment(s) from text.
 *
 * Supports both single-select and multi-select modes.
 * Multi-select activates when multiple segments are correct OR maxHighlights > 1.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
interface Segment {
  text: string;
  id: string;
  isCorrect?: boolean;
  isTappable?: boolean;
}
/** Build segments from a sentence + per-word tappable spec (audit Q3 mode tap_word_class). */
function buildSegmentsFromTappableWords(sentence: string, tappableWords: Array<{
  index: number;
  isCorrect?: boolean;
}>): Segment[] {
  if (!sentence) return [];
  const tappableMap = new Map<number, boolean>();
  for (const w of tappableWords) {
    if (typeof w?.index === 'number') {
      tappableMap.set(w.index, Boolean(w.isCorrect));
    }
  }
  const segments: Segment[] = [];
  const wordRegex = /\S+/g;
  let match: RegExpExecArray | null;
  let wordIdx = 0;
  while ((match = wordRegex.exec(sentence)) !== null) {
    const isTappable = tappableMap.has(wordIdx);
    segments.push({
      id: `word-${match.index}`,
      text: match[0],
      isCorrect: tappableMap.get(wordIdx) ?? false,
      isTappable
    });
    wordIdx++;
  }
  return segments;
}
/** Build segments from raw text + correctHighlights when explicit segments are not provided. */
function buildSegmentsFromText(text: string, correctHighlights: Array<{
  start: number;
  end: number;
} | string>): Segment[] {
  if (!text) return [];
  // Normalise: support both {start,end} objects and plain word strings
  const normalisedHighlights: Array<{
    start: number;
    end: number;
  }> = [];
  for (const h of correctHighlights) {
    if (typeof h === 'object' && h !== null && 'start' in h && 'end' in h) {
      normalisedHighlights.push(h as {
        start: number;
        end: number;
      });
    } else if (typeof h === 'string') {
      // Find all occurrences of the word in the text (case-insensitive)
      const word = h as string;
      const regex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        normalisedHighlights.push({
          start: match.index,
          end: match.index + match[0].length
        });
      }
    }
  }
  const segments: Segment[] = [];
  const wordRegex = /\S+/g;
  let match: RegExpExecArray | null;
  while ((match = wordRegex.exec(text)) !== null) {
    const wordStart = match.index;
    const wordEnd = match.index + match[0].length;
    const isCorrect = normalisedHighlights.some(h => wordStart < h.end && wordEnd > h.start);
    segments.push({
      id: `word-${wordStart}`,
      text: match[0],
      isCorrect,
      isTappable: true
    });
  }
  return segments;
}
export function TextHighlightRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const isWordClassTapMode = config.mode === 'tap_word_class';
  const segments: Segment[] = (() => {
    if (isWordClassTapMode && config.sentence && Array.isArray(config.tappableWords)) {
      return buildSegmentsFromTappableWords(config.sentence, config.tappableWords);
    }
    if (config.segments && config.segments.length > 0) return config.segments;
    if (config.text) return buildSegmentsFromText(config.text, config.correctHighlights || []);
    return [];
  })();
  // Determine if multi-select mode is needed
  const isMultiSelect = useMemo(() => {
    if (isWordClassTapMode) return true;
    if (config.maxHighlights && config.maxHighlights > 1) return true;
    const correctCount = segments.filter(s => s.isCorrect).length;
    return correctCount > 1;
  }, [config.maxHighlights, segments, isWordClassTapMode]);
  const correctCount = useMemo(() => segments.filter(s => s.isCorrect).length, [segments]);
  const prevValidateRef = useRef(false);
  // Single-select state
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelectedId(null);
    setSelectedIds(new Set());
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  // Ready state
  useEffect(() => {
    if (isMultiSelect) {
      onReadyChange?.(selectedIds.size > 0);
    } else {
      onReadyChange?.(selectedId !== null);
    }
  }, [selectedId, selectedIds, isMultiSelect, onReadyChange]);
  // Submit answer
  useEffect(() => {
    if (!shouldValidate || prevValidateRef.current) return;
    if (isMultiSelect) {
      if (selectedIds.size > 0) {
        prevValidateRef.current = true;
        onAnswer(JSON.stringify(Array.from(selectedIds)));
      }
    } else {
      if (selectedId) {
        prevValidateRef.current = true;
        onAnswer(selectedId);
      }
    }
  }, [shouldValidate, selectedId, selectedIds, isMultiSelect, onAnswer]);
  const handleClick = (segmentId: string) => {
    if (isMultiSelect) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(segmentId)) {
          next.delete(segmentId);
        } else {
          next.add(segmentId);
        }
        return next;
      });
    } else {
      setSelectedId(segmentId);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent, index: number, segment: Segment) => {
    if (segment.isTappable === false) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(segment.id);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      let next = index + 1;
      while (next < segments.length && segments[next].isTappable === false) next++;
      if (next < segments.length) document.getElementById(`segment-${next}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      let prev = index - 1;
      while (prev >= 0 && segments[prev].isTappable === false) prev--;
      if (prev >= 0) document.getElementById(`segment-${prev}`)?.focus();
    }
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-8791">
      {isWordClassTapMode && config.targetClass && <div className="mb-4 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl text-center" data-id="element-8792">
          <p className="text-sm text-indigo-700 font-medium" data-id="element-8793">
            Tik alle{' '}
            <span className="font-bold text-indigo-900" data-id="element-8794">
              {config.targetClass}
            </span>{' '}
            in de zin.
          </p>
        </div>}
      <div className="bg-white p-6 md:p-8 rounded-2xl border-2 border-slate-200 shadow-sm leading-loose text-lg md:text-xl" role="listbox" aria-multiselectable={isMultiSelect} aria-label="Kies het juiste stukje tekst" data-id="element-8795">
        {segments.map((segment, index) => {
        const isSelected = isMultiSelect ? selectedIds.has(segment.id) : selectedId === segment.id;
        if (segment.isTappable === false) {
          return <span key={segment.id || index} className="text-slate-800" data-id="element-8796">
                <LatexRenderer content={segment.text} data-id="element-8797" />
              </span>;
        }
        return <span key={segment.id || index} id={`segment-${index}`} role="option" aria-selected={isSelected} tabIndex={0} onClick={() => handleClick(segment.id)} onKeyDown={e => handleKeyDown(e, index, segment)} className={`inline-block px-1 rounded transition-colors cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${isSelected ? 'bg-indigo-600 text-white shadow-sm' : 'hover:bg-indigo-100 text-slate-800 border-b-2 border-transparent hover:border-indigo-200'}`} data-id="element-8798">
              <LatexRenderer content={segment.text} data-id="element-8799" />
            </span>;
      })}
      </div>
      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-8800">
        {isMultiSelect ? isWordClassTapMode ? 'Tik alle juiste woorden — klik om te (de)selecteren' : `Selecteer ${correctCount} woorden — klik om te (de)selecteren` : 'Klik op tekst of gebruik de pijltjestoetsen'}
      </p>
    </div>;
}